import { ErrorMessage, useFormikContext } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@src/design-system/command';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { cn } from '@src/design-system/helpers';
import { inputVariants } from '@src/design-system/input';
import { Label } from '@src/design-system/label';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@src/design-system/popover';
import type { TRule } from '@src/shared/types/rule';

type RuleSelectFormValues = {
  rule?: string;
};

type RuleSelectProps = {
  rules: Pick<TRule, 'id' | 'name'>[];
  label?: string;
  placeholder?: string;
  className?: string;
  onRuleChange?: (ruleId: string) => void;
};

export function RuleSelect({
  rules,
  label,
  placeholder = 'Select Rule',
  className,
  onRuleChange,
}: RuleSelectProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { values, setFieldValue, setFieldTouched, errors, touched } =
    useFormikContext<RuleSelectFormValues>();

  const selectedRuleId = values.rule ? String(values.rule) : '';
  const selectedRule = rules.find((rule) => String(rule.id) === selectedRuleId);

  useEffect(() => {
    if (!open) {
      setInputValue(selectedRule?.name ?? '');
    }
  }, [open, selectedRule]);

  const filteredRules = useMemo(() => {
    const query = inputValue.trim().toLowerCase();
    if (!query) return rules;

    return rules.filter((rule) =>
      (rule.name ?? '').toLowerCase().includes(query)
    );
  }, [inputValue, rules]);

  const handleSelect = (ruleId: string, ruleName: string) => {
    setFieldValue('rule', ruleId);
    if (ruleId !== selectedRuleId) onRuleChange?.(ruleId);
    setFieldTouched('rule', true, false);
    setInputValue(ruleName);
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setOpen(true);

    if (selectedRuleId && value !== selectedRule?.name) {
      setFieldValue('rule', '');
      onRuleChange?.('');
    }
  };

  return (
    <div className={className}>
      {label ? <Label>{label}</Label> : null}
      <Popover
        modal
        open={open}
        onOpenChange={setOpen}
      >
        <PopoverAnchor asChild>
          <input
            ref={inputRef}
            type='text'
            value={inputValue}
            placeholder={placeholder}
            onChange={(event) => handleInputChange(event.target.value)}
            onFocus={(event) => {
              setOpen(true);
              event.target.select();
            }}
            onClick={() => setOpen(true)}
            {...(errors.rule && touched.rule && { 'data-error': 'error' })}
            className={cn(inputVariants(), 'w-full text-sm', label && 'mt-2')}
          />
        </PopoverAnchor>
        <PopoverContent
          align='start'
          className='w-[var(--radix-popover-trigger-width)] p-0'
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <Command shouldFilter={false}>
            <CommandList className='max-h-[min(240px,var(--radix-popover-content-available-height,240px))]'>
              {filteredRules.length === 0 ? (
                <p className='py-6 text-center text-sm text-gray-500'>
                  No rules found
                </p>
              ) : (
                <CommandGroup className='p-0'>
                  {filteredRules.map((rule) => {
                    const ruleId = String(rule.id);
                    const ruleName = rule.name ?? '';
                    const isSelected = selectedRuleId === ruleId;

                    return (
                      <CommandItem
                        key={ruleId}
                        value={ruleName}
                        className={cn(
                          'py-2 block overflow-hidden bg-white last:mb-0 border-b border-primary-200 last:border-b-0 rounded-none',
                          isSelected && 'bg-primary-50'
                        )}
                        data-selected={false}
                        onSelect={() => handleSelect(ruleId, ruleName)}
                      >
                        <p
                          className={cn(
                            'text-sm text-gray-900 truncate',
                            isSelected && 'font-medium text-primary-1000'
                          )}
                        >
                          {ruleName}
                        </p>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <ErrorMessage
        name='rule'
        component={CustomErrorMessage}
      />
    </div>
  );
}
